import { Routes, Route, Navigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { ToastManager } from '@/components/ui/Toast'
import './toast-override.css'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Leads from './pages/Leads'
import AgentOrders from './pages/AgentOrders'
import ReOrderLeads from './pages/ReOrderLeads'
import RTOLeads from './pages/RTOLeads'
import Pipeline from './pages/Pipeline'
import Activities from './pages/Activities'
import Settings from './pages/Settings'
import Documentation from './pages/Documentation'
import Duplicates from './pages/Duplicates'
import OrdersProcessing from './pages/OrdersProcessing'
import LeadDetail from './pages/LeadDetail'
import ShopifyAdmin from './pages/ShopifyAdmin'
import ReportsLayout from './pages/reports/ReportsLayout'
import OrdersSummaryReport from './pages/reports/summary'
import DateWiseReport from './pages/reports/date-wise'
import TagWiseReport from './pages/reports/tag-wise'
import AgentWiseReport from './pages/reports/agent-wise'
import StatusMatrixReport from './pages/reports/status-matrix'
import AgentPerformancePage from './pages/reports/agent-performance'
import IncentiveLeaderboard from './pages/reports/incentive-leaderboard'
import AbcReport from './pages/reports/abc'
import Login from './pages/Login'
import LoginOTP from './pages/LoginOTP'
import Register from './pages/Register'
import VerifyOTP from './pages/VerifyOTP'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import SetPassword from './pages/SetPassword'
import FacebookCallback from './pages/FacebookCallback'
import GoogleCallback from './pages/GoogleCallback'
import CrmProvision from './pages/CrmProvision'
import PaymentStatus from './pages/PaymentStatus'
import PocRecordingUpload from './pages/PocRecordingUpload'
import PocAbcPreview from './pages/PocAbcPreview'
import ClaimsLayout from './pages/claims/ClaimsLayout'
import PendingClaims from './pages/claims/pending'
import AllClaims from './pages/claims/all'
import MineClaimsPage from './pages/claims/mine'
import ClaimsReports from './pages/claims/reports'
import Attendance from './pages/Attendance'
import RouteGuard from './utils/auth/RouteGuard'
import { CallingProvider } from './contexts/CallingContext'
import { UserProvider } from './contexts/UserContext'
import { UsersProvider } from './contexts/UsersContext'

function App() {
  return (
    <CallingProvider>
      <div className="App">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{ zIndex: 9999 }}
        />
        <ToastManager />
        <Routes>
        {/* Public Routes - No authentication required */}
        <Route path="/login" element={
          <RouteGuard requireAuth={false}>
            <Login />
          </RouteGuard>
        } />
        <Route path="/login-otp" element={
          <RouteGuard requireAuth={false}>
            <LoginOTP />
          </RouteGuard>
        } />
        <Route path="/register" element={
          <RouteGuard requireAuth={false}>
            <Register />
          </RouteGuard>
        } />
        <Route path="/verify-otp" element={
          <RouteGuard requireAuth={false}>
            <VerifyOTP />
          </RouteGuard>
        } />
        <Route path="/forgot-password" element={
          <RouteGuard requireAuth={false}>
            <ForgotPassword />
          </RouteGuard>
        } />
        <Route path="/reset-password" element={
          <RouteGuard requireAuth={false}>
            <ResetPassword />
          </RouteGuard>
        } />
        <Route path="/set-password" element={
          <RouteGuard requireAuth={false}>
            <SetPassword />
          </RouteGuard>
        } />
        <Route path="/auth/facebook/success" element={
          <RouteGuard requireAuth={false}>
            <FacebookCallback />
          </RouteGuard>
        } />
        <Route path="/auth/google/callback" element={
          <RouteGuard requireAuth={false}>
            <GoogleCallback />
          </RouteGuard>
        } />
        <Route path="/auth/google/success" element={
          <RouteGuard requireAuth={false}>
            <GoogleCallback />
          </RouteGuard>
        } />
        <Route path="/crm-provision" element={
          <RouteGuard requireAuth={false}>
            <CrmProvision />
          </RouteGuard>
        } />

        {/* Payment Status Page - Public (for Razorpay callbacks) */}
        <Route path="/payment/status" element={<PaymentStatus />} />

        {/* POC: Recording Upload - Public */}
        <Route path="/poc/upload-recording" element={
          <RouteGuard requireAuth={false}>
            <PocRecordingUpload />
          </RouteGuard>
        } />

        {/* POC: ABC Preview for Engati - Public */}
        <Route path="/poc/abc-preview" element={
          <RouteGuard requireAuth={false}>
            <PocAbcPreview />
          </RouteGuard>
        } />

        {/* Standalone Lead Detail Page - No Layout/Menu */}
        <Route path="/lead/:uuid" element={
          <RouteGuard requireAuth={true}>
            <UserProvider>
              <UsersProvider>
                <LeadDetail />
              </UsersProvider>
            </UserProvider>
          </RouteGuard>
        } />

        {/* Protected Routes - Authentication required */}
        <Route path="/" element={
          <RouteGuard requireAuth={true}>
            <UserProvider>
              <UsersProvider>
                <Layout />
              </UsersProvider>
            </UserProvider>
          </RouteGuard>
        }>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="leads" element={<Leads />} />
          <Route path="agent-orders" element={<AgentOrders />} />
          <Route path="reorder-leads" element={<ReOrderLeads />} />
          <Route path="rto-leads" element={<RTOLeads />} />
          <Route path="orders-processing" element={<OrdersProcessing />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="duplicates" element={<Duplicates />} />
          <Route path="activities" element={<Activities />} />
            <Route path="my-claims" element={<Navigate to="/claims" replace />} />
          <Route path="claims/*" element={<ClaimsLayout />}>
            <Route index element={<Navigate to="/claims/pending" replace />} />
            <Route path="pending" element={<PendingClaims />} />
            <Route path="all" element={<AllClaims />} />
            <Route path="mine" element={<MineClaimsPage />} />
            <Route path="reports" element={<ClaimsReports />} />
          </Route>
          <Route path="reports/*" element={
            <ReportsLayout />
          }>
            <Route index element={<Navigate to="/reports/abc" replace />} />
            <Route path="summary" element={<OrdersSummaryReport />} />
            <Route path="date-wise" element={<DateWiseReport />} />
            <Route path="tag-wise" element={<TagWiseReport />} />
            <Route path="agent-wise" element={<AgentWiseReport />} />
            <Route path="status-matrix" element={<StatusMatrixReport />} />
            <Route path="agent-performance" element={<AgentPerformancePage />} />
            <Route path="incentive-leaderboard" element={<IncentiveLeaderboard />} />
            <Route path="abc" element={<AbcReport />} />
          </Route>
          <Route path="attendance" element={<Attendance />} />
          <Route path="settings/*" element={<Settings />} />
          <Route path="shopify-admin/*" element={<ShopifyAdmin />} />
          <Route path="docs/*" element={<Documentation />} />
        </Route>
      </Routes>
      </div>
    </CallingProvider>
  )
}

export default App